(() => {
  'use strict';

  const CARD_SELECTOR = '.boost-sd__filter-product-list .boost-sd__product-item';
  const FILTER_SELECTOR = '.boost-sd__filter-tree-vertical-content';
  const STORAGE_KEY = 'ppa-results-price-view';
  const PRICE_VIEW_DESKTOP_QUERY = window.matchMedia('(min-width: 750px)');
  const productCache = new Map();
  const b2bProductRequests = document.body.classList.contains('b2b-customer')
    ? (window.PpaB2BProductRequests ||= new Map()) : null;
  let frame;

  document.querySelectorAll('[data-ppa-collection-view-switch]').forEach((control) => control.remove());
  delete document.documentElement.dataset.ppaCollectionView;
  try {
    window.localStorage.removeItem('ppa-collection-view');
  } catch (_error) {
    // The obsolete preference can be left behind when browser storage is unavailable.
  }

  const activeCurrency = () => window.Shopify?.currency?.active || 'AUD';

  const formatMoney = (cents) => {
    const documentLocale = document.documentElement.lang || 'en-AU';
    const locale = documentLocale.toLowerCase().startsWith('en') ? 'en-AU' : documentLocale;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: activeCurrency(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(cents || 0) / 100);
  };

  const currentPriceView = () =>
    document.documentElement.dataset.ppaResultsPriceView === 'unit' ? 'unit' : 'total';

  const setPriceView = (view) => {
    const nextView = PRICE_VIEW_DESKTOP_QUERY.matches && view === 'unit' ? 'unit' : 'total';
    document.documentElement.dataset.ppaResultsPriceView = nextView;

    if (PRICE_VIEW_DESKTOP_QUERY.matches) {
      try {
        window.localStorage.setItem(STORAGE_KEY, nextView);
      } catch (_error) {
        // Pricing mode still works when browser storage is unavailable.
      }
    }

    document.querySelectorAll('[data-ppa-price-view-toggle]').forEach((toggle) => {
      toggle.querySelectorAll('[data-ppa-price-view]').forEach((button) => {
        const selected = button.dataset.ppaPriceView === nextView;
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('is-active', selected);
      });
    });

    document.querySelectorAll('[data-ppa-card-commerce]').forEach(updateCommercePrices);
  };

  const restorePriceView = () => {
    let storedView = 'total';
    try {
      storedView = window.localStorage.getItem(STORAGE_KEY) || 'total';
    } catch (_error) {
      storedView = 'total';
    }
    setPriceView(storedView);
  };

  const packSizeForVariant = (variant) => {
    if (b2bProductRequests) return Math.max(1, Number(variant.pack_quantity) || 1);
    const title = String(variant.title || '');
    const sku = String(variant.sku || '');
    const packMatch = title.match(/(?:pack|carton|bundle)\s+(?:of\s+)?([\d,]+)/i);
    const skuMatch = sku.match(/\(([\d,]+)\)\s*$/);
    const looseMatch = title.match(/\b([\d,]+)\s*(?:units?|bags?|rolls?|cartons?)\b/i);
    const parsed = Number.parseInt((packMatch?.[1] || skuMatch?.[1] || looseMatch?.[1] || '1').replace(/,/g, ''), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const cleanVariantTitle = (title) => b2bProductRequests ? window.PpaB2BPricing.displayVariantTitle(title) : String(title || 'Default').replace(/\s*\(Save\s+\d+%\)\s*/i, '').trim();

  const productLink = (card) => card.querySelector('a[href*="/products/"]');

  const productKey = (href) => {
    const url = new URL(href, window.location.origin);
    return url.pathname.replace(/\/$/, '');
  };

  const fetchProduct = (href) => {
    const key = productKey(href);
    if (b2bProductRequests) return window.PpaB2BPricing.product(href);
    if (productCache.has(key)) return productCache.get(key);
    if (b2bProductRequests?.has(key)) return b2bProductRequests.get(key);

    const request = window
      .fetch(`${key}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        priority: 'low',
      })
      .then((response) => {
        if (!response.ok) throw new Error(`Product request failed with ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        productCache.delete(key);
        b2bProductRequests?.delete(key);
        throw error;
      });

    productCache.set(key, request);
    b2bProductRequests?.set(key, request);
    return request;
  };

  const createPriceToggle = () => {
    const wrapper = document.createElement('section');
    wrapper.className = 'ppa-results-price-view';
    wrapper.dataset.ppaPriceViewToggle = '';
    wrapper.setAttribute('aria-labelledby', `ppa-price-view-${Math.random().toString(36).slice(2)}`);

    const heading = document.createElement('p');
    heading.className = 'ppa-results-price-view__heading';
    heading.id = wrapper.getAttribute('aria-labelledby');
    heading.textContent = 'View prices by';

    const controls = document.createElement('div');
    controls.className = 'ppa-results-price-view__controls';
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', 'Choose how product prices are shown');

    [
      ['total', 'Total Cost'],
      ['unit', 'Unit Price'],
    ].forEach(([value, label]) => {
      const selected = value === currentPriceView();
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ppa-results-price-view__button';
      button.dataset.ppaPriceView = value;
      button.textContent = label;
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-active', selected);
      button.addEventListener('click', () => setPriceView(value));
      controls.append(button);
    });

    wrapper.append(heading, controls);
    return wrapper;
  };

  const ensurePriceToggles = () => {
    document.querySelectorAll(FILTER_SELECTOR).forEach((filterContent) => {
      if (filterContent.querySelector(':scope > [data-ppa-price-view-toggle]')) return;
      filterContent.prepend(createPriceToggle());
    });
  };

  const selectedVariant = (commerce) => {
    const product = commerce.ppaProduct;
    const select = commerce.querySelector('[data-ppa-variant-select]');
    return product?.variants?.find((variant) => String(variant.id) === select?.value) || product?.variants?.[0];
  };

  function updateCommercePrices(commerce) {
    const variant = selectedVariant(commerce);
    if (!variant) return;

    if (b2bProductRequests) {
      commerce.closest(CARD_SELECTOR)?.querySelectorAll('a[href*="/products/"]').forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set('variant', String(variant.id));
        link.href = url.href;
      });
      const card = commerce.closest(CARD_SELECTOR);
      if (card) window.PpaB2BBoostPricing?.apply(card, commerce.ppaProduct);
    }
    const view = currentPriceView();
    const rule = variant.quantity_rule || {};
    const minimum = b2bProductRequests ? Math.max(1, Number(rule.min) || 1) : 1;
    const quantity = Math.max(minimum, Number.parseInt(commerce.querySelector('[data-ppa-quantity]')?.value || String(minimum), 10));
    const packSize = packSizeForVariant(variant);
    const selectedPrice = b2bProductRequests ? window.PpaB2BPricing.priceForQuantity(variant, quantity) : Number(variant.price || 0);
    const displayedPrice = selectedPrice * quantity;
    const compareAtPrice = Number(variant.compare_at_price || 0);
    const displayedCompareAt = compareAtPrice * quantity;
    const isSale = compareAtPrice > selectedPrice;

    commerce.classList.toggle('is-sale', isSale);

    const label = commerce.querySelector('[data-ppa-price-label]');
    const value = commerce.querySelector('[data-ppa-selected-price]');
    const compare = commerce.querySelector('[data-ppa-selected-compare-price]');
    const context = commerce.querySelector('[data-ppa-price-context]');
    const mobilePack = commerce.querySelector('[data-ppa-mobile-pack]');
    const submit = commerce.querySelector('[type="submit"]');

    if (label) label.textContent = b2bProductRequests && window.PpaB2BPricing.hasBusinessPrice(variant) ? commerce.ppaProduct.businessPriceLabel : 'Total';
    if (value) value.textContent = formatMoney(displayedPrice);
    if (compare) {
      compare.textContent = compareAtPrice > selectedPrice ? formatMoney(displayedCompareAt) : '';
      compare.hidden = !(compareAtPrice > selectedPrice);
    }
    if (context) {
      const taxCopy = b2bProductRequests ? (commerce.ppaProduct.taxesIncluded ? 'inc. GST' : 'ex. GST') : 'inc. GST';
      context.textContent = `${quantity} × ${cleanVariantTitle(variant.title)}, ${taxCopy}`;
    }
    if (mobilePack) mobilePack.textContent = cleanVariantTitle(variant.title);
    const quantityInput = commerce.querySelector('[data-ppa-quantity]');
    if (b2bProductRequests && quantityInput) {
      const rule = variant.quantity_rule || {};
      quantityInput.min = String(rule.min || 1);
      quantityInput.step = String(rule.increment || 1);
      if (rule.max != null) quantityInput.max = String(rule.max); else quantityInput.removeAttribute('max');
      if (Number(quantityInput.value) < Number(quantityInput.min)) quantityInput.value = quantityInput.min;
    }
    if (submit) {
      submit.disabled = !variant.available;
      submit.querySelector('span').textContent = variant.available ? 'Add to cart' : 'Sold out';
    }

    commerce.querySelectorAll('[data-ppa-variant-row]').forEach((row) => {
      const rowVariant = commerce.ppaProduct.variants.find(
        (candidate) => String(candidate.id) === row.dataset.ppaVariantRow
      );
      if (!rowVariant) return;

      const rowPackSize = packSizeForVariant(rowVariant);
      const rowPrice = view === 'unit' ? Number(rowVariant.price || 0) / rowPackSize : Number(rowVariant.price || 0);
      const rowComparePrice =
        view === 'unit'
          ? Number(rowVariant.compare_at_price || 0) / rowPackSize
          : Number(rowVariant.compare_at_price || 0);
      const price = row.querySelector('[data-ppa-row-price]');
      const comparePrice = row.querySelector('[data-ppa-row-compare-price]');
      const unit = row.querySelector('[data-ppa-row-unit]');
      const rowIsSale = Number(rowVariant.compare_at_price || 0) > Number(rowVariant.price || 0);

      row.classList.toggle('is-selected', String(rowVariant.id) === String(variant.id));
      row.classList.toggle('is-sale', rowIsSale);
      if (price) price.textContent = formatMoney(rowPrice);
      if (comparePrice) {
        comparePrice.textContent = rowComparePrice > rowPrice ? formatMoney(rowComparePrice) : '';
        comparePrice.hidden = !(rowComparePrice > rowPrice);
      }
      if (unit) unit.textContent = b2bProductRequests && !Number(rowVariant.pack_quantity) ? cleanVariantTitle(rowVariant.title) : view === 'unit' ? 'per unit' : `pack of ${rowPackSize}`;
    });
  }

  const quantityButton = (label, delta) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ppa-results-card-commerce__quantity-button';
    button.dataset.ppaQuantityDelta = String(delta);
    button.setAttribute('aria-label', label);
    button.textContent = delta < 0 ? '−' : '+';
    return button;
  };

  const submitCommerceForm = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const variantSelect = form.querySelector('[data-ppa-variant-select]');
    const submit = form.querySelector('[type="submit"]');
    const submitText = submit?.querySelector('span:not(.loading__spinner)');
    const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
    const errorMessage = errorWrapper?.querySelector('.product-form__error-message');
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const formData = new FormData(form);

    if (submit?.disabled || submit?.getAttribute('aria-disabled') === 'true') return;

    submit?.setAttribute('aria-disabled', 'true');
    submit?.classList.add('loading');
    submit?.querySelector('.loading__spinner')?.classList.remove('hidden');
    if (submitText) submitText.textContent = 'Adding…';
    if (errorWrapper) errorWrapper.hidden = true;

    if (cart?.getSectionsToRender) {
      formData.append(
        'sections',
        cart
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',')
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement?.(submit);
    }

    try {
      if (b2bProductRequests) {
        const commerce = form.closest('[data-ppa-card-commerce]');
        const current = await window.PpaB2BPricing.product(productLink(commerce.closest(CARD_SELECTOR)).href, true);
        const variant = current.variants.find((item) => String(item.id) === String(formData.get('id')));
        const quantity = Number(formData.get('quantity'));
        const rule = variant?.quantity_rule || {};
        if (!variant?.available) throw new Error('This variant is no longer available for your buying location.');
        if (!Number.isInteger(quantity) || quantity < (rule.min || 1) || (rule.max != null && quantity > rule.max) || quantity % (rule.increment || 1) !== 0) throw new Error('Check the quantity minimum, maximum and pack increment.');
      }
      const response = await window.fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'This item could not be added to the cart.');
      }

      if (b2bProductRequests) {
        const commerce = form.closest('[data-ppa-card-commerce]');
        const href = productLink(commerce.closest(CARD_SELECTOR)).href;
        window.PpaB2BPricing.product(href, true).then((current) => {
          if (!commerce.isConnected) return;
          commerce.ppaProduct = current;
          updateCommercePrices(commerce);
        }).catch(() => {});
      }
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'ppa-results-card',
          productVariantId: formData.get('id'),
          cartData: data,
        });
      } else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'ppa-results-card',
          productVariantId: formData.get('id'),
          cartData: data,
        });
      }

      if (cart?.renderContents && data.sections) {
        cart.classList.remove('is-empty');
        cart.renderContents(data);
      } else if (!cart) {
        window.location.assign(window.routes?.cart_url || '/cart');
      }
    } catch (error) {
      if (errorMessage) errorMessage.textContent = error.message;
      if (errorWrapper) errorWrapper.hidden = false;
    } finally {
      submit?.removeAttribute('aria-disabled');
      submit?.classList.remove('loading');
      submit?.querySelector('.loading__spinner')?.classList.add('hidden');
      if (submitText) submitText.textContent = submit?.disabled ? 'Sold out' : 'Add to cart';
    }
  };

  const createCommerce = (product, card) => {
    const commerce = document.createElement('div');
    commerce.className = 'ppa-results-card-commerce';
    commerce.dataset.ppaCardCommerce = '';
    commerce.ppaProduct = product;

    const availableVariants = (product.variants || []).filter((variant) => variant.available);
    const initialVariant = (b2bProductRequests && availableVariants.find(window.PpaB2BPricing.hasBusinessPrice)) || availableVariants[0] || product.variants?.[0];
    if (!initialVariant) return null;

    const list = document.createElement('div');
    list.className = 'ppa-results-card-commerce__variants';
    list.setAttribute('aria-label', 'Available variants and prices');

    product.variants.forEach((variant) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'ppa-results-card-commerce__variant-row';
      row.dataset.ppaVariantRow = String(variant.id);
      row.disabled = !variant.available;

      const title = document.createElement('span');
      title.className = 'ppa-results-card-commerce__variant-title';
      title.textContent = cleanVariantTitle(variant.title);

      const pricing = document.createElement('span');
      pricing.className = 'ppa-results-card-commerce__variant-pricing';
      pricing.innerHTML =
        '<s data-ppa-row-compare-price hidden></s><strong data-ppa-row-price></strong><small data-ppa-row-unit></small>';

      row.append(title, pricing);
      row.addEventListener('click', () => {
        const select = commerce.querySelector('[data-ppa-variant-select]');
        if (select) {
          select.value = String(variant.id);
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      list.append(row);
    });

    const form = document.createElement('form');
    form.method = 'post';
    form.action = window.routes?.cart_add_url || '/cart/add';
    form.enctype = 'multipart/form-data';
    form.noValidate = true;
    form.dataset.type = 'add-to-cart-form';
    form.addEventListener('submit', submitCommerceForm);

    const controls = document.createElement('div');
    controls.className = 'ppa-results-card-commerce__controls';

    const quantity = document.createElement('div');
    quantity.className = 'ppa-results-card-commerce__quantity';
    quantity.append(quantityButton('Decrease quantity', -1));

    const quantityInput = document.createElement('input');
    quantityInput.type = 'number';
    quantityInput.name = 'quantity';
    quantityInput.value = '1';
    quantityInput.min = '1';
    quantityInput.step = '1';
    quantityInput.inputMode = 'numeric';
    quantityInput.dataset.ppaQuantity = '';
    quantityInput.setAttribute('aria-label', 'Quantity');
    quantity.append(quantityInput, quantityButton('Increase quantity', 1));

    const selectWrap = document.createElement('label');
    selectWrap.className = 'ppa-results-card-commerce__select-wrap';
    const visuallyHidden = document.createElement('span');
    visuallyHidden.className = 'visually-hidden';
    visuallyHidden.textContent = 'Variant';

    const select = document.createElement('select');
    select.className = 'ppa-results-card-commerce__select';
    select.name = 'id';
    select.dataset.ppaVariantSelect = '';
    product.variants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = cleanVariantTitle(variant.title);
      option.disabled = !variant.available;
      option.selected = String(variant.id) === String(initialVariant.id);
      select.append(option);
    });
    selectWrap.append(visuallyHidden, select);
    controls.append(quantity, selectWrap);

    const summary = document.createElement('div');
    summary.className = 'ppa-results-card-commerce__summary';
    summary.innerHTML = `
      <div>
        <span class="ppa-results-card-commerce__price-label" data-ppa-price-label></span>
        <span class="ppa-results-card-commerce__mobile-from" aria-hidden="true">From</span>
        <div class="ppa-results-card-commerce__price-line">
          <s data-ppa-selected-compare-price hidden></s>
          <strong data-ppa-selected-price></strong>
        </div>
        <small class="ppa-results-card-commerce__mobile-pack" data-ppa-mobile-pack></small>
      </div>
      <small data-ppa-price-context></small>
    `;

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.name = 'add';
    submit.className = 'ppa-results-card-commerce__add button button--full-width';
    submit.setAttribute('aria-haspopup', 'dialog');
    submit.innerHTML = `
      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 4h2l2.2 11h10.6L20 7H7M9 20a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm8 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"/></svg>
      <span>Add to cart</span>
      <span class="loading__spinner hidden" aria-hidden="true"></span>
    `;

    const error = document.createElement('div');
    error.className = 'product-form__error-message-wrapper';
    error.setAttribute('role', 'alert');
    error.hidden = true;
    error.innerHTML = '<span class="product-form__error-message"></span>';

    select.addEventListener('change', () => updateCommercePrices(commerce));
    quantityInput.addEventListener('input', () => {
      const parsed = Number.parseInt(quantityInput.value || '1', 10);
      quantityInput.value = String(Number.isFinite(parsed) && parsed >= Number(quantityInput.min) ? parsed : Number(quantityInput.min));
      updateCommercePrices(commerce);
    });
    quantity.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ppa-quantity-delta]');
      if (!button) return;
      const nextQuantity = Math.max(Number(quantityInput.min), Number.parseInt(quantityInput.value || quantityInput.min, 10) + Number(button.dataset.ppaQuantityDelta) * Number(quantityInput.step));
      if (quantityInput.max && nextQuantity > Number(quantityInput.max)) return;
      quantityInput.value = String(nextQuantity);
      updateCommercePrices(commerce);
    });

    form.append(controls, summary, submit, error);
    commerce.append(list, form);
    commerce.addEventListener('click', (event) => event.stopPropagation());
    commerce.addEventListener('mousedown', (event) => event.stopPropagation());

    updateCommercePrices(commerce);
    card.classList.add('ppa-results-card--commerce-ready');
    return commerce;
  };

  const enhanceCard = async (card) => {
    const link = productLink(card);
    if (!link) return;

    const key = productKey(link.href);
    if (card.dataset.ppaCommerceProduct === key && card.dataset.ppaCommerceState) return;

    card.dataset.ppaCommerceProduct = key;
    card.dataset.ppaCommerceState = 'loading';

    try {
      const product = await fetchProduct(link.href);
      if (!card.isConnected || card.dataset.ppaCommerceProduct !== key) return;

      const info = card.querySelector('.boost-sd__product-info');
      if (!info) throw new Error('Product information container was not found');

      card.querySelector('[data-ppa-card-commerce]')?.remove();
      const commerce = createCommerce(product, card);
      if (commerce) {
        info.append(commerce);
        if (b2bProductRequests) window.PpaB2BBoostPricing?.apply(card, product);
      }
      card.dataset.ppaCommerceState = commerce ? 'ready' : 'unavailable';
    } catch (error) {
      card.dataset.ppaCommerceState = 'error';
      // Native/contextual failures must never fall back to an indexed price.
      if (b2bProductRequests) window.PpaB2BBoostPricing?.enhance(card);
    }
  };

  const enhance = () => {
    frame = null;
    ensurePriceToggles();
    document.querySelectorAll(CARD_SELECTOR).forEach(enhanceCard);
  };

  const queueEnhance = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(enhance);
  };

  restorePriceView();
  queueEnhance();

  PRICE_VIEW_DESKTOP_QUERY.addEventListener('change', () => {
    restorePriceView();
    queueEnhance();
  });

  const observer = new MutationObserver((records) => {
    const hasExternalChange = records.some((record) => {
      const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
      return !target?.closest?.('[data-ppa-card-commerce]');
    });

    if (hasExternalChange) queueEnhance();
  });
  observer.observe(document.querySelector('#MainContent') || document.body, { childList: true, subtree: true });
})();
